// Couple Profile Management Page
// - Create Couple Profile by inviting a partner
// - View and edit shared couple profile details
// - Manage couple bundle subscription
// - View shared wallet status

import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  ArrowLeft, 
  Users, 
  Heart, 
  Send, 
  Check, 
  X, 
  Loader2, 
  Wallet, 
  Crown, 
  Edit2, 
  LogOut,
  Mail,
  Clock,
  Sparkles,
  AlertCircle
} from "lucide-react";
import { AuthContext } from "../App";
import apiClient from "../api/client";
import { toast } from "sonner";
import { GlobalNav } from "../components/GlobalNav";
import InlineNotice from "../components/common/InlineNotice";


// Couple Type Labels
const COUPLE_TYPE_LABELS = {
  mf: "Man + Woman",
  ff: "Woman + Woman",
  mixed: "Mixed / Other"
};

export default function CoupleProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useContext(AuthContext);
  
  // State
  const [loading, setLoading] = useState(true);
  const [couple, setCouple] = useState(null);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    invite_email: "",
    couple_type: "mf",
    display_name: "",
    bio: "",
    looking_for: []
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    display_name: "",
    bio: "",
    looking_for: [],
    couple_type: ""
  });
  
  // Fetch couple data
  const fetchCoupleData = async () => {
    try {
      setLoading(true);
      
      const [coupleRes, invitesRes] = await Promise.all([
        apiClient.get(`/couples/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        apiClient.get(`/couples/pending-invites`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCouple(coupleRes.data.couple);
      setPendingInvites(invitesRes.data.invites || []);
      
      // If couple exists, populate edit form
      if (coupleRes.data.couple) {
        setEditForm({
          display_name: coupleRes.data.couple.display_name || "",
          bio: coupleRes.data.couple.bio || "",
          looking_for: coupleRes.data.couple.looking_for || [],
          couple_type: coupleRes.data.couple.couple_type || "mf"
        });
      }
    } catch (err) {
      console.error("Failed to fetch couple data:", err);
      toast.error("Failed to load couple profile");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (token) {
      fetchCoupleData();
    }
  }, [token]);
  
  // Handle success redirect from checkout
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      toast.success("Couple Bundle purchased! Refreshing...");
      fetchCoupleData();
    }
    
    const canceled = searchParams.get("canceled");
    if (canceled) {
      toast.info("Checkout canceled");
    }
  }, [searchParams]);
  
  // Create couple profile
  const handleCreateCouple = async (e) => {
    e.preventDefault();
    
    if (!createForm.invite_email.trim()) {
      toast.error("Please enter your partner's email");
      return;
    }
    
    try {
      setSubmitting(true);
      
      const res = await apiClient.post(`/couples/invite`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invite sent to ${createForm.invite_email}!`);
      setShowCreateForm(false);
      fetchCoupleData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create couple profile";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Accept invite
  const handleAcceptInvite = async (invite) => {
    try {
      setSubmitting(true);
      
      await apiClient.post(`/couples/accept`, {
        invite_token: invite.invite_token
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Welcome to your couple profile!");
      fetchCoupleData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to accept invite";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Decline invite
  const handleDeclineInvite = async (invite) => {
    try {
      await apiClient.post(`/couples/decline`, {
        invite_token: invite.invite_token
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Invite declined");
      fetchCoupleData();
    } catch (err) {
      toast.error("Failed to decline invite");
    }
  };
  
  // Update couple profile
  const handleUpdateCouple = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      await apiClient.put(`/couples/me`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Couple profile updated!");
      setShowEditForm(false);
      fetchCoupleData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to update profile";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Leave couple
  const handleLeaveCouple = async () => {
    if (!window.confirm("Are you sure you want to leave this couple profile? This action cannot be undone.")) {
      return;
    }
    
    try {
      await apiClient.post(`/couples/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("You have left the couple profile");
      fetchCoupleData();
    } catch (err) {
      toast.error("Failed to leave couple");
    }
  };
  
  // Cancel pending invite
  const handleCancelInvite = async () => {
    if (!window.confirm("Cancel this invite and delete the pending couple profile?")) {
      return;
    }
    
    try {
      await apiClient.post(`/couples/cancel-invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Invite cancelled");
      fetchCoupleData();
    } catch (err) {
      toast.error("Failed to cancel invite");
    }
  };
  
  // Purchase couple bundle
  const handlePurchaseBundle = async () => {
    if (!couple?.id) {
      toast.error("No active couple profile");
      return;
    }
    
    try {
      setSubmitting(true);
      
      const res = await apiClient.post(`/couples/billing/checkout`, {
        couple_id: couple.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to start checkout";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
        <GlobalNav />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      <GlobalNav />
      
      <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-gray-300" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Couple Profile</h1>
            <p className="text-sm text-gray-400">
              Link two accounts into one shared profile
            </p>
          </div>
        </div>
        
        {/* Pending Invites Banner */}
        {pendingInvites.length > 0 && (
          <div className="mb-6 space-y-3">
            {pendingInvites.map((invite) => (
              <div 
                key={invite.invite_token}
                className="bg-gradient-to-r from-pink-900/50 to-purple-900/50 border border-pink-500/30 rounded-xl p-4"
                data-testid="pending-invite-banner"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-pink-400 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-medium">
                        Couple Invite from {invite.inviter?.display_name || "Someone"}
                      </p>
                      <p className="text-sm text-gray-300 mt-1">
                        Join as a {COUPLE_TYPE_LABELS[invite.couple_type] || invite.couple_type} couple
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAcceptInvite(invite)}
                      disabled={submitting}
                      className="px-3 py-1.5 bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
                      data-testid="accept-invite-btn"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeclineInvite(invite)}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition"
                      data-testid="decline-invite-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* No Couple - Show Create Option */}
        {!couple && !showCreateForm && (
          <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-8 text-center">
            <Users className="w-16 h-16 text-pink-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Create a Couple Profile
            </h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Link two individual accounts into one shared couple profile. 
              Both partners maintain their own identity while sharing a public couple presence.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium rounded-xl hover:from-pink-700 hover:to-purple-700 transition"
              data-testid="create-couple-btn"
            >
              <Heart className="w-5 h-5" />
              Create Couple Profile
            </button>
          </div>
        )}
        
        {/* Create Couple Form */}
        {showCreateForm && (
          <form onSubmit={handleCreateCouple} className="space-y-6">
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-pink-400" />
                Create Couple Profile
              </h2>
              
              {/* Partner Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Partner&apos;s Email *
                </label>
                <input
                  type="email"
                  value={createForm.invite_email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, invite_email: e.target.value }))}
                  placeholder="partner@example.com"
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-3 text-white placeholder-gray-500 focus:border-pink-500 focus:outline-none"
                  required
                  data-testid="invite-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  They&apos;ll receive an invitation to join your couple profile
                </p>
              </div>
              
              {/* Couple Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Couple Composition
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: "mf", label: "Man + Woman" },
                    { value: "ff", label: "Woman + Woman" },
                    { value: "mixed", label: "Mixed / Other" }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, couple_type: option.value }))}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                        createForm.couple_type === option.value
                          ? "bg-pink-600 text-white"
                          : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                      }`}
                      data-testid={`create-couple-type-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Display Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Couple Display Name (optional)
                </label>
                <input
                  type="text"
                  value={createForm.display_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="e.g., Alex & Jamie"
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-3 text-white placeholder-gray-500 focus:border-pink-500 focus:outline-none"
                  data-testid="create-display-name-input"
                />
              </div>
              
              {/* Bio */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  About Us (optional)
                </label>
                <textarea
                  value={createForm.bio}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Tell others about your couple..."
                  rows={3}
                  className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-3 text-white placeholder-gray-500 focus:border-pink-500 focus:outline-none resize-none"
                  data-testid="create-bio-input"
                />
              </div>
              
              <InlineNotice
                type="info"
                message="Your partner will need to accept the invite to activate the couple profile."
              />
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="flex-1 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-600 transition font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-xl hover:from-pink-700 hover:to-purple-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="send-invite-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Invite
                  </>
                )}
              </button>
            </div>
          </form>
        )}
        
        {/* Pending Couple (waiting for partner) */}
        {couple && couple.status === "pending" && (
          <div className="space-y-6">
            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Waiting for Partner
                  </h3>
                  <p className="text-gray-300">
                    Your couple profile is pending. An invite has been sent to{" "}
                    <span className="text-yellow-400 font-medium">
                      {couple.pending_invite?.invite_email}
                    </span>
                  </p>
                  {couple.pending_invite?.expires_at && (
                    <p className="text-sm text-gray-500 mt-2">
                      Expires: {new Date(couple.pending_invite.expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={handleCancelInvite}
                className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition"
              >
                Cancel Invite
              </button>
            </div>
          </div>
        )}
        
        {/* Active Couple Profile */}
        {couple && couple.status === "active" && (
          <div className="space-y-6">
            {/* Couple Card */}
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl overflow-hidden">
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-pink-600/30 to-purple-600/30 px-6 py-4 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-pink-600/30 flex items-center justify-center">
                      <Users className="w-6 h-6 text-pink-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {couple.display_name || "Your Couple"}
                      </h2>
                      <p className="text-sm text-pink-300">
                        {COUPLE_TYPE_LABELS[couple.couple_type] || couple.couple_type}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEditForm(!showEditForm)}
                    className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700 transition"
                    data-testid="edit-couple-btn"
                  >
                    <Edit2 className="w-5 h-5 text-gray-300" />
                  </button>
                </div>
              </div>
              
              {/* Partners Info */}
              <div className="p-6">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Partners</h3>
                <div className="flex gap-4">
                  {/* Partner A */}
                  <div className="flex-1 flex items-center gap-3 bg-gray-800/50 rounded-lg p-3">
                    <div className="w-10 h-10 rounded-full bg-purple-600/30 flex items-center justify-center">
                      {couple.partner_a?.avatar_url ? (
                        <img src={couple.partner_a.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-purple-400 font-medium">
                          {couple.partner_a?.display_name?.[0] || "A"}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">
                        {couple.partner_a?.display_name || couple.partner_a?.names?.[0] || "Partner A"}
                      </p>
                      {couple.is_partner_a && (
                        <span className="text-xs text-purple-400">(You)</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Partner B */}
                  <div className="flex-1 flex items-center gap-3 bg-gray-800/50 rounded-lg p-3">
                    <div className="w-10 h-10 rounded-full bg-pink-600/30 flex items-center justify-center">
                      {couple.partner_b?.avatar_url ? (
                        <img src={couple.partner_b.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <span className="text-pink-400 font-medium">
                          {couple.partner_b?.display_name?.[0] || "B"}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">
                        {couple.partner_b?.display_name || couple.partner_b?.names?.[0] || "Partner B"}
                      </p>
                      {!couple.is_partner_a && (
                        <span className="text-xs text-pink-400">(You)</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Bio */}
                {couple.bio && (
                  <div className="mt-4">
                    <h3 className="text-sm font-medium text-gray-400 mb-2">About</h3>
                    <p className="text-gray-300">{couple.bio}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Edit Form */}
            {showEditForm && (
              <form onSubmit={handleUpdateCouple} className="bg-gray-900/60 border border-gray-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Edit Couple Profile</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={editForm.display_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
                      className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-3 text-white focus:border-pink-500 focus:outline-none"
                      data-testid="edit-display-name-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      About Us
                    </label>
                    <textarea
                      value={editForm.bio}
                      onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                      rows={3}
                      className="w-full rounded-lg bg-gray-800 border border-gray-600 px-4 py-3 text-white focus:border-pink-500 focus:outline-none resize-none"
                      data-testid="edit-bio-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Couple Type
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: "mf", label: "Man + Woman" },
                        { value: "ff", label: "Woman + Woman" },
                        { value: "mixed", label: "Mixed / Other" }
                      ].map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setEditForm(prev => ({ ...prev, couple_type: option.value }))}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                            editForm.couple_type === option.value
                              ? "bg-pink-600 text-white"
                              : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditForm(false)}
                    className="flex-1 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition disabled:opacity-50"
                    data-testid="save-couple-btn"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Save Changes"}
                  </button>
                </div>
              </form>
            )}
            
            {/* Couple Bundle Section */}
            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 border border-purple-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-6 h-6 text-yellow-400" />
                <h3 className="text-lg font-semibold text-white">Couple Bundle</h3>
              </div>
              
              {couple.subscription?.status === "active" ? (
                // Active subscription
                <div>
                  <div className="flex items-center gap-2 text-green-400 mb-3">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Active Subscription</span>
                  </div>
                  
                  {/* Wallet Status */}
                  {couple.wallet && (
                    <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400 text-sm">Shared Wallet</span>
                        <Wallet className="w-4 h-4 text-purple-400" />
                      </div>
                      <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold text-white">
                          {Math.max(0, (couple.wallet.allowance_units || 0) - (couple.wallet.used_units || 0))}
                        </span>
                        <span className="text-gray-400 mb-1">
                          / {couple.wallet.allowance_units || 0} minutes
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Shared premium pool for both partners.
                      </p>
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-400">
                    Both partners have Elite benefits active.
                  </p>
                </div>
              ) : (
                // No subscription - show purchase CTA
                <div>
                  <p className="text-gray-300 mb-4">
                    Shared premium pool for both partners.
                  </p>
                  <ul className="space-y-2 mb-6">
                    {[
                      "Elite benefits for both partners",
                      "Shared usage pool (8 hours/month)",
                      "See who liked your couple profile",
                      "Priority matching for couples"
                    ].map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <Sparkles className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-between bg-gray-900/50 rounded-lg p-4 mb-4">
                    <div>
                      <p className="text-white font-semibold">$29.99<span className="text-gray-400 font-normal">/month</span></p>
                      <p className="text-xs text-green-400">Save vs. 2× individual plans!</p>
                    </div>
                    <button
                      onClick={handlePurchaseBundle}
                      disabled={submitting}
                      className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg hover:from-yellow-600 hover:to-orange-600 transition disabled:opacity-50"
                      data-testid="purchase-bundle-btn"
                    >
                      {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Get Bundle"}
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Leave Couple */}
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">Leave Couple</h3>
              <p className="text-sm text-gray-400 mb-4">
                Leaving will dissolve the couple profile. This cannot be undone.
              </p>
              <button
                onClick={handleLeaveCouple}
                className="px-4 py-2 bg-red-600/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-600/30 transition flex items-center gap-2"
                data-testid="leave-couple-btn"
              >
                <LogOut className="w-4 h-4" />
                Leave Couple
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
